import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "../App";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import axios from "axios";
import { toast } from "sonner";
import { Upload, X, ImageIcon, Loader2, Check, AlertCircle, MessageSquare } from "lucide-react";

export default function NewAnalysis() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [storeName, setStoreName] = useState("");
  const [referenceImage, setReferenceImage] = useState(null);
  const [currentImage, setCurrentImage] = useState(null);
  const [referencePreview, setReferencePreview] = useState(null);
  const [currentPreview, setCurrentPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [feedbackClassification, setFeedbackClassification] = useState("");
  const [feedbackComment, setFeedbackComment] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [submittingFeedback, setSubmittingFeedback] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCategories(response.data);
    } catch (error) {
      toast.error("Erro ao carregar categorias");
    }
  };

  const handleImageChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error("Por favor, selecione uma imagem válida");
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        if (type === 'reference') {
          setReferenceImage(file);
          setReferencePreview(event.target.result);
        } else {
          setCurrentImage(file);
          setCurrentPreview(event.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = useCallback((e, type) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (type === 'reference') {
          setReferenceImage(file);
          setReferencePreview(event.target.result);
        } else {
          setCurrentImage(file);
          setCurrentPreview(event.target.result);
        }
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const clearImage = (type) => {
    if (type === 'reference') {
      setReferenceImage(null);
      setReferencePreview(null);
    } else {
      setCurrentImage(null);
      setCurrentPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCategory) {
      toast.error("Selecione uma categoria");
      return;
    }
    if (!storeName.trim()) {
      toast.error("Informe o nome da loja");
      return;
    }
    if (!referenceImage || !currentImage) {
      toast.error("Adicione as duas imagens");
      return;
    }

    setLoading(true);
    setAnalysisResult(null);

    const formData = new FormData();
    formData.append('category_id', selectedCategory);
    formData.append('store_name', storeName.trim());
    formData.append('reference_image', referenceImage);
    formData.append('current_image', currentImage);

    try {
      const response = await axios.post(`${API}/analysis`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setAnalysisResult(response.data);
      toast.success("Análise concluída!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao realizar análise");
    } finally {
      setLoading(false);
    }
  };

  const getOccupancyColor = (value) => {
    if (value >= 80) return "#34C759";
    if (value >= 50) return "#FF9500";
    return "#FF3B30";
  };

  const getClassificationColor = (classification) => {
    switch (classification) {
      case 'BOA': return "#34C759";
      case 'RUIM': return "#FF9500";
      case 'VAZIA': return "#FF3B30";
      case 'CRITICA': return "#8B0000";
      default: return "#6B7280";
    }
  };

  const getClassificationLabel = (classification) => {
    switch (classification) {
      case 'BOA': return "Boa";
      case 'RUIM': return "Ruim";
      case 'VAZIA': return "Vazia";
      case 'CRITICA': return "Crítica";
      default: return classification;
    }
  };

  const handleFeedbackSubmit = async () => {
    if (!feedbackClassification) {
      toast.error("Selecione uma classificação");
      return;
    }

    setSubmittingFeedback(true);
    try {
      await axios.post(
        `${API}/analysis/${analysisResult.id}/feedback`,
        {
          user_classification: feedbackClassification,
          user_comment: feedbackComment
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFeedbackSubmitted(true);
      toast.success("Feedback enviado! Obrigado por ajudar a melhorar a IA.");
    } catch (error) {
      toast.error("Erro ao enviar feedback");
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const ImageUploadZone = ({ type, preview, onDrop, onChange, onClear }) => (
    <div className="relative">
      {preview ? (
        <div className="relative border-2 border-zinc-200 overflow-hidden bg-zinc-50">
          <img 
            src={preview} 
            alt={type === 'reference' ? 'Imagem de referência' : 'Imagem atual'}
            className="w-full h-64 object-contain"
          />
          <button
            type="button"
            onClick={() => onClear(type)}
            className="absolute top-2 right-2 w-8 h-8 bg-white border border-zinc-200 flex items-center justify-center hover:bg-zinc-100"
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <label
          onDrop={(e) => onDrop(e, type)}
          onDragOver={handleDragOver}
          className="flex flex-col items-center justify-center h-64 border-2 border-dashed border-zinc-300 cursor-pointer hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
        >
          <input
            type="file"
            accept="image/*"
            onChange={(e) => onChange(e, type)}
            className="hidden"
            data-testid={`upload-${type}-input`}
          />
          <Upload className="w-10 h-10 text-zinc-400 mb-3" strokeWidth={1.5} />
          <p className="text-sm font-medium text-zinc-600 mb-1">
            Arraste ou clique para enviar
          </p>
          <p className="text-xs text-zinc-400">PNG, JPG até 10MB</p>
        </label>
      )}
    </div>
  );

  return (
    <Sidebar>
      <div className="p-6 lg:p-8 max-w-5xl" data-testid="new-analysis-page">
        {/* Header */}
        <div className="mb-8">
          <h1 className="font-chivo font-bold text-3xl tracking-tight mb-1">Nova Análise</h1>
          <p className="text-zinc-500 text-sm">
            Compare a imagem de referência com o estado atual para medir a ocupação
          </p>
        </div>

        {analysisResult ? (
          /* Result View */
          <div className="bg-white border border-zinc-200 p-8" data-testid="analysis-result">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-green-100 flex items-center justify-center">
                <Check className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h2 className="font-chivo font-bold text-xl">Análise Concluída</h2>
                <p className="text-sm text-zinc-500">{analysisResult.store_name} - {analysisResult.category_name}</p>
              </div>
            </div>

            {/* Classification Display */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center py-6 bg-zinc-50 border border-zinc-200">
                <p className="text-sm text-zinc-500 mb-2">Classificação</p>
                <p 
                  className="font-chivo font-black text-3xl"
                  style={{ color: getClassificationColor(analysisResult.classification) }}
                  data-testid="classification-result"
                >
                  {getClassificationLabel(analysisResult.classification)}
                </p>
              </div>
              <div className="text-center py-6 bg-zinc-50 border border-zinc-200">
                <p className="text-sm text-zinc-500 mb-2">Ocupação</p>
                <p 
                  className="font-chivo font-black text-3xl"
                  style={{ color: getOccupancyColor(analysisResult.occupancy_percentage) }}
                  data-testid="occupancy-result"
                >
                  {analysisResult.occupancy_percentage}%
                </p>
              </div>
            </div>

            {/* Empty Shelves Count */}
            {analysisResult.empty_shelves_count > 0 && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200">
                <p className="text-sm text-red-800">
                  <span className="font-bold">{analysisResult.empty_shelves_count}</span> prateleira(s) completamente vazia(s) detectada(s)
                </p>
              </div>
            )}

            {/* Analysis Details */}
            <div className="mb-6">
              <h3 className="font-medium text-sm mb-2">Detalhes da Análise</h3>
              <p className="text-sm text-zinc-600 leading-relaxed bg-zinc-50 p-4 border border-zinc-200">
                {analysisResult.analysis_details}
              </p>
            </div>

            {/* Images Comparison */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div>
                <p className="text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">Referência</p>
                <div className="border border-zinc-200 bg-zinc-50">
                  <img src={referencePreview} alt="Referência" className="w-full h-48 object-contain" />
                </div>
              </div>
              <div>
                <p className="text-xs font-medium text-zinc-500 mb-2 uppercase tracking-wider">Atual</p>
                <div className="border border-zinc-200 bg-zinc-50">
                  <img src={currentPreview} alt="Atual" className="w-full h-48 object-contain" />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 mb-6">
              <Button
                onClick={() => {
                  setAnalysisResult(null);
                  setReferenceImage(null);
                  setReferencePreview(null);
                  setCurrentImage(null);
                  setCurrentPreview(null);
                  setSelectedCategory("");
                  setStoreName("");
                  setFeedbackClassification("");
                  setFeedbackComment("");
                  setFeedbackSubmitted(false);
                }}
                data-testid="new-analysis-again-btn"
                className="flex-1 h-11 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white"
              >
                Nova Análise
              </Button>
              <Button
                onClick={() => navigate(`/analysis/${analysisResult.id}`)}
                variant="outline"
                className="flex-1 h-11 rounded-none border-zinc-200"
              >
                Ver Detalhes
              </Button>
            </div>

            {/* Feedback Section */}
            <div className="border-t border-zinc-200 pt-6">
              <div className="flex items-center gap-2 mb-4">
                <MessageSquare size={18} className="text-zinc-500" />
                <h3 className="font-chivo font-bold text-lg">Ajude a melhorar a IA</h3>
              </div>
              
              {feedbackSubmitted ? (
                <div className="bg-green-50 border border-green-200 p-4 text-center">
                  <Check className="w-6 h-6 text-green-600 mx-auto mb-2" />
                  <p className="text-sm text-green-800 font-medium">Feedback registrado!</p>
                  <p className="text-xs text-green-600 mt-1">Obrigado por contribuir para a melhoria contínua.</p>
                </div>
              ) : (
                <div className="bg-zinc-50 border border-zinc-200 p-4">
                  <p className="text-sm text-zinc-600 mb-4">
                    A IA classificou como <strong style={{ color: getClassificationColor(analysisResult.classification) }}>
                    {getClassificationLabel(analysisResult.classification)}</strong>. Concorda? Se não, qual seria a classificação correta?
                  </p>
                  
                  <div className="grid grid-cols-4 gap-2 mb-4">
                    {['BOA', 'RUIM', 'VAZIA', 'CRITICA'].map((cls) => (
                      <button
                        key={cls}
                        onClick={() => setFeedbackClassification(cls)}
                        data-testid={`feedback-${cls.toLowerCase()}`}
                        className={`p-3 border text-sm font-medium transition-all ${
                          feedbackClassification === cls
                            ? 'border-zinc-900 bg-zinc-900 text-white'
                            : 'border-zinc-200 hover:border-zinc-400'
                        }`}
                      >
                        {getClassificationLabel(cls)}
                      </button>
                    ))}
                  </div>
                  
                  <div className="mb-4">
                    <Label className="text-xs text-zinc-500 mb-2 block">Comentário (opcional)</Label>
                    <Textarea
                      value={feedbackComment}
                      onChange={(e) => setFeedbackComment(e.target.value)}
                      placeholder="Ex: A prateleira do meio estava completamente vazia..."
                      className="rounded-none resize-none text-sm"
                      rows={2}
                      data-testid="feedback-comment"
                    />
                  </div>
                  
                  <Button
                    onClick={handleFeedbackSubmit}
                    disabled={!feedbackClassification || submittingFeedback}
                    data-testid="submit-feedback-btn"
                    className="w-full h-10 rounded-none bg-blue-600 hover:bg-blue-700 text-white"
                  >
                    {submittingFeedback ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Enviando...
                      </span>
                    ) : (
                      "Enviar Feedback"
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* Form View */
          <form onSubmit={handleSubmit}>
            {/* Store Name & Category */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white border border-zinc-200 p-6">
                <Label className="text-sm font-medium mb-3 block">Nome da Loja *</Label>
                <Input
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  placeholder="Ex: Loja Centro, Filial Shopping Norte"
                  className="h-11 rounded-none"
                  data-testid="store-name-input"
                />
              </div>

              <div className="bg-white border border-zinc-200 p-6">
                <Label className="text-sm font-medium mb-3 block">Categoria *</Label>
                {categories.length === 0 ? (
                  <div className="flex items-center gap-2 text-sm text-amber-600 bg-amber-50 p-4 border border-amber-200">
                    <AlertCircle size={18} />
                    <span>Nenhuma categoria. </span>
                    <button
                      type="button"
                      onClick={() => navigate('/categories')}
                      className="font-medium underline"
                    >
                      Criar
                    </button>
                  </div>
                ) : (
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger data-testid="category-select" className="h-11 rounded-none">
                      <SelectValue placeholder="Selecione uma categoria" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {/* Image Upload */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-white border border-zinc-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ImageIcon size={18} className="text-zinc-400" />
                  <Label className="text-sm font-medium">Imagem de Referência</Label>
                </div>
                <p className="text-xs text-zinc-500 mb-4">
                  A gôndola/refrigerador vazio ou no estado máximo de ocupação
                </p>
                <ImageUploadZone
                  type="reference"
                  preview={referencePreview}
                  onDrop={handleDrop}
                  onChange={handleImageChange}
                  onClear={clearImage}
                />
              </div>

              <div className="bg-white border border-zinc-200 p-6">
                <div className="flex items-center gap-2 mb-4">
                  <ImageIcon size={18} className="text-zinc-400" />
                  <Label className="text-sm font-medium">Imagem Atual</Label>
                </div>
                <p className="text-xs text-zinc-500 mb-4">
                  O estado atual da gôndola/refrigerador com produtos
                </p>
                <ImageUploadZone
                  type="current"
                  preview={currentPreview}
                  onDrop={handleDrop}
                  onChange={handleImageChange}
                  onClear={clearImage}
                />
              </div>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              data-testid="submit-analysis-btn"
              disabled={loading || !selectedCategory || !storeName.trim() || !referenceImage || !currentImage}
              className="w-full h-12 rounded-none bg-zinc-900 hover:bg-zinc-800 text-white font-medium disabled:opacity-50"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analisando imagens...
                </span>
              ) : (
                "Analisar Ocupação"
              )}
            </Button>
          </form>
        )}
      </div>
    </Sidebar>
  );
}
